import { Controller } from "@hotwired/stimulus";
import { getJSON } from "../api.js";
import { actButton } from "../icons.js";
import { fillCell, fillRow, hrefFromPattern, getPath } from "../data_table_formats.js";
import { appendParams, buildFilterBar, emptyState, hasActive, initialState, ownedParams, stateFromSearch } from "../data_table_filters.js";

// Reusable, configurable data table. ONE config object, three equal sources merged in this order (later wins):
//   (a) data-attributes on the root        data-data-table-url-value, -params-value, -page-size-value, ...
//   (b) JSON in the template                {{ config|json_script:"<id>" }} + data-data-table-config-id-value="<id>"
//   (c) JSON from an API                    data-data-table-config-url-value (fetched once, before the first load)
// Config keys (v1):
//   url          list endpoint; asked with ?format=<format>&page&page_size&search, answers {rows,total,page,page_size,pages}
//                (a bare array -> in-memory table)
//   format       response format name sent as `format` (default "ls-table"; "" = send none, endpoint answers a bare list)
//   params       fixed query params, e.g. {"organization": 3} (merged key by key across sources)
//   searchParam  query parameter the search text is sent as (default "search"; the clients API uses "q")
//   pageSize     rows per page (default 25), sent as page_size
//   searchable   show the search box (debounced 300 ms, sends `search`)
//   compact      preview mode: no pager, `ls-dt--compact` styling (pair with pageSize 5, searchable false)
//   lazy         wait for load() (a hidden tab costs nothing)
//   emptyText    text of the empty state
//   columns[]    {key, label, format, align, fallback?, href?, sub?:{key, format}}; format = money|date|datetime|text|
//                pill:<map>|label:<map>|plural:a,b,c|prefix:<str>. `width` is reserved (not applied yet).
//                href "/management/klienci/{organization_id}/" makes the cell a link (plain text when a field is empty).
//   filters[]    filter bar above the table (see static/js/data_table_filters.js): {key, label, type, options, default?, ...} with
//                type segment | chips | daterange | combobox (a segment may carry `default`: its state without the parameter). Any change -> page 1 + refetch; „Wyczyść filtry" resets.
//   syncUrl      mirror search + filters in the page URL (history.replaceState) and read them on connect, so a link like
//                ?status=paid deep-links. Default false (a page may host several tables; only one may sync).
//   rowActions[] {label, icon, variant?, hrefPattern | event, visibleIf?}; hrefPattern "/x/?id={id}" renders a
//                link; event dispatches `data-table:row-action` with detail {name, item, row, button}.
// Events (dispatch() bubbles to window):
//   data-table:loaded      after each remote page render, detail {query, total}; `query` = search + filters + fixed params as
//                          a query string (no page / page_size / format) — a page figure (payment totals) refetches with it.
//   data-table:row-action  see rowActions[] above.
// Without `columns` the markup supplies <thead> and a <template data-data-table-target="rowTemplate">.
// REMOTE rows are fetched per page/search/filters; LOCAL rows (setRows() or a bare-array response) page and search
// in memory (filters apply to remote tables only). State lives in the controller; the URL only with syncUrl.
//
// TODO (extension points, intentionally not built): column sorting (columns[].sortable -> `ordering` param),
// row selection (config.selectable + `data-table:select`),
// column visibility (config.columns[].hidden + a toggle menu), per-column width (columns[].width).

const SEARCH_DEBOUNCE_MS = 300;
const SKELETON_ROWS = 3;
const DEFAULTS = { format: "ls-table", pageSize: 25, searchable: false, lazy: false, emptyText: "Brak wyników.", columns: [], rowActions: [], params: {}, filters: [], syncUrl: false };

// Page numbers + "…" gaps for a pager: [1, 2, 3, 4, "…", 10]. Everything when there are <= 7 pages.
export function pageWindow(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 3) return [1, 2, 3, 4, "…", total];
  if (current >= total - 2) return [1, "…", total - 3, total - 2, total - 1, total];
  return [1, "…", current - 1, current, current + 1, "…", total];
}

// Later source wins per key; params merge key by key.
export function mergeConfig(...sources) {
  const out = {};
  for (const src of sources) {
    if (!src || typeof src !== "object") continue;
    for (const [key, value] of Object.entries(src)) {
      if (value === undefined) continue;
      out[key] = key === "params" ? { ...(out.params || {}), ...value } : value;
    }
  }
  return out;
}

export default class extends Controller {
  static targets = ["head", "body", "status", "pager", "info", "pages", "search", "searchBar", "filters", "rowTemplate"];

  static values = {
    url: String,
    params: Object,
    pageSize: Number,
    searchable: Boolean,
    lazy: Boolean,
    emptyText: String,
    configId: String,
    configUrl: String,
  };

  #cfg = { ...DEFAULTS };
  #ready = Promise.resolve();
  #page = 1;
  #query = "";
  #total = 0;
  #started = false;
  #seq = 0;
  #timer = null;
  #local = null; // [{ el, text }] when the table works on in-memory rows
  #fState = {}; // filter values, see data_table_filters.js
  #urlRead = false;
  #onCombo = (event) => this.#comboChanged(event);

  connect() {
    this.#cfg = mergeConfig(DEFAULTS, this.#attrConfig(), this.#jsonConfig());
    window.addEventListener("combobox:change", this.#onCombo);
    this.#applyConfig();
    if (this.hasConfigUrlValue && this.configUrlValue) {
      this.#ready = getJSON(this.configUrlValue)
        .then((remote) => {
          this.#cfg = mergeConfig(this.#cfg, remote);
          this.#applyConfig();
        })
        .catch(() => {}); // a failed config fetch keeps (a)+(b)
    }
    if (!this.#cfg.lazy && this.#cfg.url) this.load();
  }

  disconnect() {
    window.removeEventListener("combobox:change", this.#onCombo);
    clearTimeout(this.#timer);
    this.#seq += 1; // a response that lands after removal is dropped
  }

  // First call fetches; later calls do nothing (a hidden tab costs nothing until it is opened).
  async load() {
    if (this.#started) return;
    this.#started = true;
    await this.#ready;
    await this.#refresh();
  }

  // Force a fetch of the current page (after a mutation elsewhere on the page).
  async reload() {
    this.#started = true;
    await this.#ready;
    await this.#refresh();
  }

  // In-memory rows (payload the page already has). Keeps the current search; goes back to page 1.
  async setRows(items) {
    this.#started = true;
    await this.#ready;
    this.#seq += 1;
    this.#local = items.map((item) => {
      const el = this.#buildRow(item);
      const text = [...el.querySelectorAll("td:not(.ls-dt__actions)")].map((c) => c.textContent).join(" ").toLowerCase();
      return { el, text };
    });
    this.#page = 1;
    this.#renderLocal();
  }

  // ---- actions ----------------------------------------------------------------------------------

  search() {
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => {
      this.#query = this.hasSearchTarget ? this.searchTarget.value.trim() : "";
      this.#page = 1;
      this.#writeUrl();
      if (this.#started) this.#refresh();
    }, SEARCH_DEBOUNCE_MS);
  }

  retry() {
    this.#refresh();
  }

  goTo(page) {
    if (page === this.#page || page < 1 || page > this.#pageCount) return;
    this.#page = page;
    this.#refresh();
  }

  // ---- config -----------------------------------------------------------------------------------

  #attrConfig() {
    const out = {};
    if (this.hasUrlValue) out.url = this.urlValue;
    if (this.hasParamsValue) out.params = this.paramsValue;
    if (this.hasPageSizeValue) out.pageSize = this.pageSizeValue;
    if (this.hasSearchableValue) out.searchable = this.searchableValue;
    if (this.hasLazyValue) out.lazy = this.lazyValue;
    if (this.hasEmptyTextValue) out.emptyText = this.emptyTextValue;
    return out;
  }

  #jsonConfig() {
    if (!this.hasConfigIdValue || !this.configIdValue) return {};
    const node = document.getElementById(this.configIdValue);
    if (!node) return {};
    try {
      return JSON.parse(node.textContent || "{}");
    } catch {
      return {};
    }
  }

  #applyConfig() {
    this.element.classList.toggle("ls-dt--compact", Boolean(this.#cfg.compact));
    if (this.hasSearchBarTarget) this.searchBarTarget.hidden = !this.#cfg.searchable;
    if (this.#cfg.columns.length && this.hasHeadTarget) this.#renderHead();
    this.#initFilters();
  }

  // ---- filters ----------------------------------------------------------------------------------

  #initFilters() {
    const filters = this.#cfg.filters;
    if (this.#cfg.syncUrl && !this.#urlRead) {
      this.#urlRead = true;
      const params = new URLSearchParams(window.location.search);
      this.#fState = stateFromSearch(filters, params);
      const q = (params.get("search") || "").trim();
      if (q) {
        this.#query = q;
        if (this.hasSearchTarget) this.searchTarget.value = q;
      }
    } else {
      const kept = {};
      for (const key of Object.keys(emptyState(filters))) if (key in this.#fState) kept[key] = this.#fState[key];
      this.#fState = { ...initialState(filters), ...kept };
    }
    this.#renderFilters();
  }

  #renderFilters() {
    if (!this.hasFiltersTarget) return;
    const filters = this.#cfg.filters;
    this.filtersTarget.hidden = filters.length === 0;
    if (!filters.length) {
      this.filtersTarget.replaceChildren();
      return;
    }
    this.filtersTarget.replaceChildren(
      buildFilterBar(filters, this.#fState, {
        onChange: () => this.#filtersChanged(),
        onReset: () => this.#resetFilters(),
      }),
    );
  }

  #filtersChanged() {
    this.#page = 1;
    this.#writeUrl();
    if (this.#started) this.#refresh();
  }

  #resetFilters() {
    this.#fState = emptyState(this.#cfg.filters);
    this.#renderFilters(); // fresh bar: radios, chips, dates and comboboxes all start empty
    this.#filtersChanged();
  }

  #comboChanged(event) {
    const detail = event.detail || {};
    if (detail.silent || !this.element.isConnected) return;
    const filter = this.#cfg.filters.find((f) => f.type === "combobox" && f.key === detail.name);
    if (!filter || !this.element.querySelector(`[data-controller="combobox"][data-combobox-name-value="${filter.key}"]`)) return;
    this.#fState[filter.key] = detail.value === null || detail.value === undefined ? "" : String(detail.value);
    const reset = this.hasFiltersTarget ? this.filtersTarget.querySelector(".ls-dt__reset") : null;
    if (reset) reset.hidden = !hasActive(this.#cfg.filters, this.#fState);
    this.#filtersChanged();
  }

  #writeUrl() {
    if (!this.#cfg.syncUrl) return;
    const url = new URL(window.location.href);
    for (const name of [...ownedParams(this.#cfg.filters), "search"]) url.searchParams.delete(name);
    appendParams(this.#cfg.filters, this.#fState, url.searchParams, { explicitEmpty: true });
    if (this.#query) url.searchParams.set("search", this.#query);
    window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
  }

  #renderHead() {
    const tr = document.createElement("tr");
    for (const col of this.#cfg.columns) {
      const th = document.createElement("th");
      th.scope = "col";
      th.textContent = col.label || "";
      if (col.align === "right") th.className = "ls-dt__num";
      tr.append(th);
    }
    if (this.#cfg.rowActions.length) {
      const th = document.createElement("th");
      th.scope = "col";
      th.className = "ls-dt__actions";
      const label = document.createElement("span");
      label.className = "sr-only";
      label.textContent = "Akcje";
      th.append(label);
      tr.append(th);
    }
    this.headTarget.replaceChildren(tr);
  }

  // ---- loading ----------------------------------------------------------------------------------

  get #pageSize() {
    return this.#cfg.pageSize;
  }

  get #pageCount() {
    return Math.max(1, Math.ceil(this.#total / this.#pageSize));
  }

  #refresh() {
    if (this.#local) {
      this.#renderLocal();
      return Promise.resolve();
    }
    return this.#fetchPage();
  }

  #buildUrl() {
    const url = new URL(this.#cfg.url, window.location.origin);
    for (const [key, value] of Object.entries(this.#cfg.params)) {
      if (value !== null && value !== undefined && value !== "") url.searchParams.set(key, String(value));
    }
    appendParams(this.#cfg.filters, this.#fState, url.searchParams);
    if (this.#cfg.format) url.searchParams.set("format", this.#cfg.format);
    url.searchParams.set("page", String(this.#page));
    url.searchParams.set("page_size", String(this.#pageSize));
    if (this.#query) url.searchParams.set(this.#cfg.searchParam || "search", this.#query);
    return url.pathname + url.search;
  }

  // The request's query without paging/format: search + filters + fixed params (what a page figure must follow).
  #filterQuery(requestUrl) {
    const query = new URL(requestUrl, window.location.origin).searchParams;
    for (const name of ["page", "page_size", "format"]) query.delete(name);
    return query.toString();
  }

  async #fetchPage() {
    this.#seq += 1;
    const seq = this.#seq;
    this.#setLoading(true);
    try {
      const requestUrl = this.#buildUrl();
      const data = await getJSON(requestUrl);
      if (seq !== this.#seq) return;
      if (Array.isArray(data)) {
        await this.setRows(data);
        return;
      }
      // ls-table answers {rows,total}; a plain DRF page answers {results,count} (format "" endpoints).
      const results = Array.isArray(data.rows) ? data.rows : Array.isArray(data.results) ? data.results : [];
      const total = Number.isFinite(data.total) ? data.total : data.count;
      this.#total = Number.isFinite(total) ? total : results.length;
      const lastPage = Math.max(1, Number.isFinite(data.pages) ? Math.min(data.pages, this.#pageCount) : this.#pageCount);
      if (this.#page > lastPage) {
        this.#page = lastPage;
        await this.#fetchPage();
        return;
      }
      this.bodyTarget.replaceChildren(...results.map((item) => this.#buildRow(item)));
      this.#showResult(results.length);
      this.dispatch("loaded", { detail: { query: this.#filterQuery(requestUrl), total: this.#total } });
    } catch (error) {
      if (seq !== this.#seq) return;
      if (error?.status === 404 && this.#page > 1) {
        this.#page = 1;
        await this.#fetchPage();
        return;
      }
      this.#showError();
    } finally {
      if (seq === this.#seq) this.#setLoading(false);
    }
  }

  #renderLocal() {
    const q = this.#query.toLowerCase();
    const rows = q ? this.#local.filter((r) => r.text.includes(q)) : this.#local;
    this.#total = rows.length;
    this.#page = Math.min(this.#page, this.#pageCount);
    const from = (this.#page - 1) * this.#pageSize;
    const slice = rows.slice(from, from + this.#pageSize);
    this.bodyTarget.replaceChildren(...slice.map((r) => r.el));
    this.#showResult(slice.length);
    this.#setLoading(false);
  }

  // ---- rendering --------------------------------------------------------------------------------

  #buildRow(item) {
    const row = this.#cfg.columns.length ? this.#rowFromColumns(item) : this.#rowFromTemplate(item);
    if (item.id !== null && item.id !== undefined) row.dataset.rowId = String(item.id);
    return row;
  }

  #rowFromTemplate(item) {
    const row = this.rowTemplateTarget.content.firstElementChild.cloneNode(true);
    fillRow(row, item);
    return row;
  }

  #rowFromColumns(item) {
    const tr = document.createElement("tr");
    for (const col of this.#cfg.columns) {
      const td = document.createElement("td");
      const isPill = (col.format || "").startsWith("pill:");
      const cell = isPill ? document.createElement("span") : td;
      cell.dataset.field = col.key;
      if (col.format) cell.dataset.format = col.format;
      if (col.fallback) cell.dataset.fallback = col.fallback;
      if (col.align === "right") td.classList.add("ls-dt__num");
      if (isPill) td.append(cell);
      fillCell(cell, item);
      if (col.href && !isPill) this.#linkCell(td, col, item);
      if (col.sub) td.append(this.#subLine(col.sub, item));
      tr.append(td);
    }
    if (this.#cfg.rowActions.length) tr.append(this.#actionsCell(tr, item));
    return tr;
  }

  // Wraps the filled cell text in a link; stays plain text when a placeholder field of the pattern is empty.
  #linkCell(td, col, item) {
    const href = hrefFromPattern(col.href, item);
    if (href === null) return;
    const a = document.createElement("a");
    a.className = "ls-dt__link";
    a.setAttribute("href", href);
    a.textContent = td.textContent;
    td.replaceChildren(a);
  }

  #subLine(sub, item) {
    const line = document.createElement("div");
    line.className = "ls-dt__muted";
    line.dataset.field = sub.key;
    if (sub.format) line.dataset.format = sub.format;
    fillCell(line, item);
    line.hidden = !getPath(item, sub.key);
    return line;
  }

  #actionsCell(row, item) {
    const td = document.createElement("td");
    td.className = "ls-dt__actions";
    const wrap = document.createElement("div");
    wrap.className = "ls-row-actions ls-act-row";
    for (const action of this.#cfg.rowActions) {
      if (action.visibleIf && !getPath(item, action.visibleIf)) continue;
      const variant = action.variant || "neutral";
      if (action.hrefPattern) {
        const href = hrefFromPattern(action.hrefPattern, item);
        if (href === null) continue;
        const a = actButton({ label: action.label, iconName: action.icon, variant, tag: "a" });
        a.setAttribute("href", href);
        wrap.append(a);
      } else {
        const btn = actButton({ label: action.label, iconName: action.icon, variant });
        btn.addEventListener("click", () => {
          this.dispatch("row-action", { detail: { name: action.event, item, row, button: btn } });
        });
        wrap.append(btn);
      }
    }
    td.append(wrap);
    return td;
  }

  #setLoading(on) {
    this.element.classList.toggle("is-loading", on);
    this.element.setAttribute("aria-busy", on ? "true" : "false");
    if (!on) return;
    this.statusTarget.hidden = true;
    if (this.bodyTarget.querySelector("tr:not(.ls-dt__skel)")) return; // keep old rows, dimmed
    const cols = this.element.querySelectorAll("thead th").length || 1;
    const rows = [];
    for (let i = 0; i < SKELETON_ROWS; i += 1) {
      const tr = document.createElement("tr");
      tr.className = "ls-dt__skel";
      const td = document.createElement("td");
      td.colSpan = cols;
      const bar = document.createElement("span");
      bar.className = "ls-dt__skel-bar";
      td.append(bar);
      tr.append(td);
      rows.push(tr);
    }
    this.bodyTarget.replaceChildren(...rows);
  }

  #showResult(shown) {
    this.statusTarget.replaceChildren();
    if (this.#total === 0) {
      this.statusTarget.textContent = this.#query
        ? `Brak wyników dla „${this.#query}".`
        : this.#cfg.emptyText;
      this.statusTarget.hidden = false;
      this.pagerTarget.hidden = true;
      return;
    }
    this.statusTarget.hidden = true;
    const from = (this.#page - 1) * this.#pageSize + 1;
    this.infoTarget.textContent = `Pozycje ${from}–${from + shown - 1} z ${this.#total}`;
    this.#renderPages();
    this.pagerTarget.hidden = Boolean(this.#cfg.compact);
  }

  #renderPages() {
    const pages = this.#pageCount;
    if (pages <= 1) {
      this.pagesTarget.replaceChildren();
      return;
    }
    const nodes = [this.#pageButton("‹", this.#page - 1, "Poprzednia strona", this.#page === 1)];
    for (const entry of pageWindow(this.#page, pages)) {
      if (entry === "…") {
        const gap = document.createElement("span");
        gap.className = "ls-dt__gap";
        gap.textContent = "…";
        nodes.push(gap);
      } else {
        const btn = this.#pageButton(String(entry), entry, `Strona ${entry}`, false);
        if (entry === this.#page) {
          btn.classList.add("is-active");
          btn.setAttribute("aria-current", "page");
        }
        nodes.push(btn);
      }
    }
    nodes.push(this.#pageButton("›", this.#page + 1, "Następna strona", this.#page === pages));
    this.pagesTarget.replaceChildren(...nodes);
  }

  #pageButton(label, page, aria, disabled) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "ls-page";
    btn.textContent = label;
    btn.setAttribute("aria-label", aria);
    if (disabled) {
      btn.disabled = true;
      btn.classList.add("is-disabled");
    } else {
      btn.addEventListener("click", () => this.goTo(page));
    }
    return btn;
  }

  #showError() {
    this.bodyTarget.replaceChildren();
    this.pagerTarget.hidden = true;
    const msg = document.createElement("p");
    msg.textContent = "Nie udało się załadować danych.";
    const retry = document.createElement("button");
    retry.type = "button";
    retry.className = "ls-btn ls-btn--ghost ls-btn--sm";
    retry.textContent = "Spróbuj ponownie";
    retry.addEventListener("click", () => this.retry());
    this.statusTarget.replaceChildren(msg, retry);
    this.statusTarget.hidden = false;
  }
}
